export function normalizeDate(value: unknown): Date | undefined {
  if (!(value instanceof Date)) return undefined;
  if (Number.isNaN(value.getTime())) return undefined;
  if (value.getUTCFullYear() <= 1) return undefined;

  return value;
}

type TimeSpanLike = {
  ticks?: number;
  days?: number;
  hours?: number;
  milliseconds?: number;
  minutes?: number;
  seconds?: number;
  totalDays?: number;
  totalHours?: number;
  totalMilliseconds?: number;
  totalMinutes?: number;
  totalSeconds?: number;
};

const TIME_SPAN_KEYS = new Set([
  'ticks',
  'days',
  'hours',
  'milliseconds',
  'minutes',
  'seconds',
  'totalDays',
  'totalHours',
  'totalMilliseconds',
  'totalMinutes',
  'totalSeconds',
]);

export function isTimeSpanLike(value: unknown): value is TimeSpanLike {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length === 0) return false;

  if (!keys.every(key => TIME_SPAN_KEYS.has(key))) return false;

  const hasNumericDuration = keys.some(key => typeof record[key] === 'number');
  if (!hasNumericDuration) return false;

  return keys.every(key => record[key] === undefined || typeof record[key] === 'number');
}

export function formatTimeSpan(value: TimeSpanLike): string {
  let totalSeconds: number;

  if (typeof value.totalMilliseconds === 'number') {
    totalSeconds = Math.floor(value.totalMilliseconds / 1000);
  } else if (typeof value.totalSeconds === 'number') {
    totalSeconds = Math.floor(value.totalSeconds);
  } else if (typeof value.totalMinutes === 'number') {
    totalSeconds = Math.floor(value.totalMinutes * 60);
  } else if (typeof value.totalHours === 'number') {
    totalSeconds = Math.floor(value.totalHours * 3600);
  } else if (typeof value.totalDays === 'number') {
    totalSeconds = Math.floor(value.totalDays * 86400);
  } else {
    const days = value.days ?? 0;
    const hours = value.hours ?? 0;
    const minutes = value.minutes ?? 0;
    const seconds = value.seconds ?? 0;

    totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }

  if (!Number.isFinite(totalSeconds)) return '-';

  const absolute = Math.abs(totalSeconds);
  const hours = Math.floor(absolute / 3600);
  const minutes = Math.floor((absolute % 3600) / 60);
  const seconds = Math.floor(absolute % 60);
  const sign = totalSeconds < 0 ? '-' : '';

  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
    seconds
  ).padStart(2, '0')}`;
}

export function formatTimeSpanValue(value: unknown): string | undefined {
  if (!isTimeSpanLike(value)) return undefined;

  return formatTimeSpan(value);
}
